import { useEffect, useRef, useState } from "react";

// The scene board is server-built and cheap to re-read (the GET is a
// `since=` delta). The tick is 5 s, the classic transcript's cadence, so the
// first planned version reaches the page as fast as classic's next sentence
// (TT-1133); once a board exists and no job is running it changes every
// few minutes, so the poll then takes every other tick. The tick itself
// never changes — the interval lives in an effect whose re-run would drop
// the held series.
//
// The board is the live cassette's plan (platform TT-1270): the read is the
// cassette's current plan in the board's shape. The advance is a nudge to
// its planner, and the feed carries one only while the cassette does not
// plan itself (liveFeedClient `boardFeed`); a feed without it only reads.
// The server owns the nudge's cost guards (warm-up, min-new-sentences
// window, atomic job claim) and answers 202 with the job state.
const POLL_MS = 5000;
const SETTLED_EVERY = 2; // ticks between polls once a board exists and nothing is running

// A queued or running job that has not gone stale; a closed meeting keeps
// polling only while one is still finishing its board.
const ACTIVE = new Set(["queued", "running"]);

// appback camelizes top-level response keys, but the conversion is shallow —
// this is the one board field where the casings genuinely diverge.
export function costUsd(board) {
  return Number(board?.costUsd ?? board?.cost_usd ?? 0);
}

/**
 * The one poll/advance cycle both live-system views share (TT-1046): GET
 * the scene board every 5 s through the feed client, POST the advance nudge
 * alongside when the feed carries one (not a viewer's link client, TT-1110,
 * nor a feed whose cassette plans itself), and
 * dedupe unchanged responses so the vendored solvers don't re-run for the
 * life of a meeting.
 *
 * `archetype` picks the server-side planner ("story" for the scene system,
 * "board" for the board system). For the board archetype the hook also
 * drives the versions delta protocol: it sends `since=<held count>` so a
 * poll re-downloads nothing, and accumulates the series in a referentially
 * stable array that changes identity only when it grows.
 *
 * `closed` stops the poll once the meeting has ended and no job is active —
 * a finished board never changes, and a viewer tab may sit on it for days.
 *
 * Returns { board, versions, job, jobError, fetchError, fetchStatus }.
 */
export default function useSceneBoardPoll(
  feed,
  archetype,
  { errorFallback = "The board builder hit an error", closed = false } = {}
) {
  const [board, setBoard] = useState(null);
  const [fetchError, setFetchError] = useState("");
  const [fetchStatus, setFetchStatus] = useState(null);
  const versionsRef = useRef(null);
  // The latest render's inputs, read by the tick (so a change in them never
  // restarts the poll): whether the doc is closed and the board as fetched.
  const latest = useRef({ closed, board });
  latest.current = { closed, board };

  useEffect(() => {
    if (!feed) return undefined;
    let cancelled = false;
    // One in-flight cycle at a time — a slow response must not stack requests.
    // Effect-local on purpose: a doc change starts a fresh effect run whose
    // first poll must not be blocked by the previous doc's in-flight cycle.
    let busy = false;
    let tick = 0;
    let fetched = false;
    setBoard(null);
    setFetchError("");
    versionsRef.current = null;

    async function poll() {
      const { closed: isClosed, board: cur } = latest.current;
      const running = ACTIVE.has(cur?.job?.status);
      // A closed doc is done once its board has been read (a viewer opening
      // a finished meeting must still get that one fetch — TT-1150) and no
      // job runs. Settled (a board on the page, nothing running) polls at
      // half cadence.
      if (busy || (isClosed && fetched && !running)) return;
      const settled = !!(cur?.spec || versionsRef.current?.length) && !running;
      if (settled && tick++ % SETTLED_EVERY) return;
      busy = true;
      try {
        const held = versionsRef.current?.length ?? 0;
        const resp = await feed.getSceneBoard(held);
        fetched = true;
        if (!cancelled) {
          if (resp && !resp.error) {
            const total = resp.versionsTotal ?? resp.versions_total;
            if (total != null && total < held) {
              // The board shrank (reset): our held series is stale. Drop it;
              // the next poll asks from 0 and rebuilds.
              versionsRef.current = null;
            } else if (resp.versions?.length) {
              versionsRef.current = [...(versionsRef.current || []), ...resp.versions];
            }
            // Keep the previous object when nothing changed (same upto, same
            // job status) so downstream memos hold for the life of a meeting.
            setBoard((prev) =>
              prev && prev.upto === resp.upto && (prev.job?.status ?? "") === (resp.job?.status ?? "") ? prev : resp
            );
            setFetchError("");
            setFetchStatus(null);
          } else if (resp?.status !== 404) {
            // 404 just means no board yet — the empty state covers it.
            setFetchError(resp?.msg || "Unable to load the board");
            setFetchStatus(resp?.status || null);
          }
        }
        // Nudge the planner; the server's guards decide whether anything
        // runs. Job progress/errors arrive via the GET. A viewer's client
        // has no advance, nor does a feed whose cassette plans itself.
        if (feed.advanceSceneBoard && !isClosed) {
          const adv = await feed.advanceSceneBoard(archetype);
          if (!cancelled && adv && !adv.error && adv.job) {
            setBoard((prev) => (prev ? { ...prev, job: adv.job } : prev));
          }
        }
      } catch (err) {
        console.log("scene-board poll failed:", err);
      } finally {
        busy = false;
      }
    }

    poll();
    const id = setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [feed, archetype]);

  const job = board?.job || null;
  const jobError = job?.status === "error" ? job?.error || job?.message || errorFallback : "";
  return { board, versions: versionsRef.current, job, jobError, fetchError, fetchStatus };
}
